import {
  getAllShoppingContextKeywords,
  getAllShoppingSubcategoryKeywords,
  getLocaleAwareShoppingContextKeywords,
  getLocaleAwareSubcategoryKeywords,
  getShoppingSubcategoryPriority,
} from './languages';
import {
  type SubcategoryConfig,
  type SubcategoryResult,
  detectSubcategory,
} from './subcategoryDetection';
import { EXPENSE_TAXONOMY } from './taxonomy';

export const SHOPPING_KEYWORDS = getAllShoppingSubcategoryKeywords();

export const SHOPPING_CONTEXT_KEYWORDS = {
  clothes: getAllShoppingContextKeywords('clothes'),
  electronics: getAllShoppingContextKeywords('electronics'),
  furniture: getAllShoppingContextKeywords('furniture'),
  online: getAllShoppingContextKeywords('online'),
  groceries: getAllShoppingContextKeywords('groceries'),
  services: getAllShoppingContextKeywords('services'),
};

/** Derived from the canonical `EXPENSE_TAXONOMY`. See taxonomy.ts. */
export const VALID_SHOPPING_SUBCATEGORIES =
  EXPENSE_TAXONOMY.shopping as readonly string[] as string[];

export const SHOPPING_CONFIG: SubcategoryConfig = {
  keywords: SHOPPING_KEYWORDS,
  validSubcategories: VALID_SHOPPING_SUBCATEGORIES,
  contextKeywords: SHOPPING_CONTEXT_KEYWORDS,
  minConfidenceThreshold: 0.3,
  subcategoryPriority: getShoppingSubcategoryPriority(),
};

export function detectShoppingSubcategory(description: string): string {
  return detectSubcategory(description, SHOPPING_CONFIG).subcategory;
}

export function detectShoppingSubcategoryWithConfidence(
  description: string
): SubcategoryResult {
  return detectSubcategory(description, SHOPPING_CONFIG);
}

/**
 * Locale-aware shopping subcategory detector. Combines global brand
 * keywords (Walmart, Carrefour, Zara, Sephora, Home Depot, Galeries
 * Lafayette, Sodimac, etc.) with the user's primary-language common-
 * word vocabulary. Returns `default@0` when locale doesn't resolve so
 * cloud LLM can step in.
 */
export function detectShoppingSubcategoryWithLocale(
  description: string,
  locale: string | undefined | null
): SubcategoryResult {
  const keywords = getLocaleAwareSubcategoryKeywords(
    b => b.shoppingSubcategories,
    k => k.shoppingSubcategories,
    locale
  );
  if (!keywords) {
    return { subcategory: 'default', confidence: 0, matchedKeywords: [] };
  }
  // Shopping context vocabulary ('mode/boutique' FR, 'kleidung' DE,
  // 'moda/vestuário' PT) is per-language. Use only the user's locale's
  // context, not the cross-language merge.
  const localeContextKeywords = {
    clothes: getLocaleAwareShoppingContextKeywords('clothes', locale) ?? [],
    electronics:
      getLocaleAwareShoppingContextKeywords('electronics', locale) ?? [],
    furniture: getLocaleAwareShoppingContextKeywords('furniture', locale) ?? [],
    online: getLocaleAwareShoppingContextKeywords('online', locale) ?? [],
    groceries: getLocaleAwareShoppingContextKeywords('groceries', locale) ?? [],
    services: getLocaleAwareShoppingContextKeywords('services', locale) ?? [],
  };
  return detectSubcategory(description, {
    ...SHOPPING_CONFIG,
    keywords,
    contextKeywords: localeContextKeywords,
  });
}
